import React, { useState, useEffect, useMemo } from 'react';
import { Progress } from 'components/ui';
import {
  UserIcon,
  ClipboardDocumentListIcon,
  CreditCardIcon,
  CheckCircleIcon,
  EnvelopeIcon,
  DocumentTextIcon,
  SparklesIcon,
  ArrowUpTrayIcon,
  CalendarIcon,
  BriefcaseIcon,
  UserGroupIcon,
  BanknotesIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';
import { getLeadJourneyIndicators } from 'services/leadsService';
import { getLeadPaymentLinks } from 'services/paymentServices';

/**
 * Reusable Progress Journey Component
 * Displays a progress stepper for application/lead progression
 * Fetches journey indicators from API if leadId is provided
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} props.leadId - Lead ID to fetch journey indicators (optional)
 * @param {number} props.currentStep - Current active step (1-11)
 * @param {Object} props.completedSteps - Object with step IDs as keys and boolean values indicating completion
 * @param {Function} props.onStepClick - Callback function when a step is clicked
 * @param {boolean} props.isClickable - Whether steps are clickable (default: true)
 * @param {boolean} props.isMobile - Whether to use mobile-optimized layout (default: false)
 * @returns {JSX.Element} Progress journey component
 *
 * Step IDs:
 *  1  - Application Details
 *  3  - Offer Letter Generated  (hidden for India)
 *  4  - Payment Link (application fee)
 *  5  - Payment Successful (application fee)
 *  8  - Interview Scheduled
 *  6  - Application Initiated
 *  7  - Application Submitted
 *  9  - Fee Payment Link              ← NEW
 *  10 - Fee Payment (Success/Pending) ← NEW
 *  2  - Document Upload
 */

const JOURNEY_STEPS = [
  {
    id: 1,
    label: 'Application Details',
    shortLabel: 'Details',
    icon: <UserIcon className="h-5 w-5" />,
    description: 'Basic information collected',
  },
  {
    id: 3,
    label: 'Offer Letter Generated',
    shortLabel: 'Offer',
    icon: <BriefcaseIcon className="h-5 w-5" />,
    description: 'Offer letter has been generated',
  },
  {
    id: 4,
    label: 'Payment Link',
    shortLabel: 'Payment Link',
    icon: <EnvelopeIcon className="h-5 w-5" />,
    description: 'Payment link sent to your email',
  },
  {
    id: 5,
    label: 'Payment Successful',
    shortLabel: 'Payment Done',
    icon: <CreditCardIcon className="h-5 w-5" />,
    description: 'Payment processed and verified',
  },
  {
    id: 8,
    label: 'Interview Scheduled',
    shortLabel: 'Interview',
    icon: <CalendarIcon className="h-5 w-5" />,
    description: 'Interview has been scheduled',
  },
  {
    id: 6,
    label: 'Application Initiated',
    shortLabel: 'Initiated',
    icon: <ClipboardDocumentListIcon className="h-5 w-5" />,
    description: 'Application has been initiated',
  },
  {
    id: 7,
    label: 'Application Submitted',
    shortLabel: 'Submitted',
    icon: <SparklesIcon className="h-5 w-5" />,
    description: 'Application successfully submitted',
  },
  // ── NEW STEPS ──────────────────────────────────────────────

  {
    id: 9,
    label: 'Fee Payment Link',
    shortLabel: 'Fee Link',
    icon: <LinkIcon className="h-5 w-5" />,
    description: 'Fee payment link sent',
  },
  {
    id: 10,
    label: 'Fee Payment',
    shortLabel: 'Fee Payment',
    icon: <BanknotesIcon className="h-5 w-5" />,
    description: 'Fee payment status',
  },
  // ────────────────────────────────────────────────────────────
  {
    id: 2,
    label: 'Document Upload',
    shortLabel: 'Documents',
    icon: <ArrowUpTrayIcon className="h-5 w-5" />,
    description: 'Required documents submitted',
  },
];

const ProgressJourney = ({
  leadId = null,
  currentStep = 1,
  completedSteps = {},
  onStepClick = () => {},
  isClickable = true,
  isMobile = false,
  progressData = {},
  setProgressData = () => {},
  leadCountry = null,
}) => {
  const [loading, setLoading] = useState(false);
  const [hasPaidFeeLink, setHasPaidFeeLink] = useState(false);

  // Fetch journey indicators from API if leadId is provided
  useEffect(() => {
    if (leadId) {
      setLoading(true);
      // Fetch indicators and payment links in parallel to ensure accurate status
      Promise.all([
        getLeadJourneyIndicators(leadId),
        getLeadPaymentLinks(leadId)
      ])
        .then(([indicatorsRes, paymentsRes]) => {
          if (indicatorsRes.success && indicatorsRes.data) {
            setProgressData(indicatorsRes.data);
          }
          
          if (paymentsRes.success && paymentsRes.data) {
            const links = paymentsRes.data || [];
            const hasPaid = links.some(link => 
              link.LinkStatus?.toUpperCase() === 'PAID' && 
              (link.IsEnrollmentFee === true || link.IsAdmissionFee === true || link.GatewayCode === 'MAN')
            );
            setHasPaidFeeLink(hasPaid);
          }
        })
        .catch((error) => {
          console.error('Error fetching progress data:', error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [leadId]);

  // Calculate completed steps from API data
  const apiCompletedSteps = useMemo(() => {
    if (!progressData || Object.keys(progressData).length === 0) return completedSteps;

    const completed = { ...completedSteps };

    // Step 1: Lead Details - Always completed (initial step)
    completed[1] = true;

    // Step 2: Document Upload - Check IsAllDocumentsVerified
    if (progressData?.IsAllDocumentsVerified) {
      completed[2] = true;
    }

    // Step 3: Offer Letter Generated - Check IsOfferLetterGenerated
    if (progressData?.IsOfferLetterGenerated) {
      completed[3] = true;
    }

    // Step 4: Payment Link - Check IsPaymentLinkCreated
    if (progressData.IsPaymentLinkGenerated) {
      completed[4] = true;
    }

    // Step 5: Payment Successful - Check IsApplicationPaymentDone
    if (progressData.IsApplicationPaymentDone) {
      completed[5] = true;
    }

    // Step 6: Application Initiated - Check IsApplicationInitiated
    if (progressData.IsApplicationInitiated) {
      completed[6] = true;
    }

    // Step 7: Application Submitted - Check IsApplicationSubmitted
    if (progressData.ApplicationCompletionPercentage === 100 && progressData.IsApplicationSubmitted) {
      completed[7] = true;
    }

    // Step 8: Interview Scheduled - Check IsInterviewScheduled
    if (progressData?.IsInterviewScheduled) {
      completed[8] = true;
    }

    // Step 9: Fee Payment Link Sent ← NEW
    if (progressData?.IsFeePaymentLinkGenerated || progressData?.IsFeePaymentDone || hasPaidFeeLink) {
      completed[9] = true;
    }

    // Step 10: Fee Payment Done ← NEW
    // Mark complete only when fee payment is confirmed successful (API or Paid link)
    if (progressData?.IsFeePaymentDone || hasPaidFeeLink) {
      completed[10] = true;
    }

    return completed;
  }, [progressData, completedSteps]);

  // Use API-derived completed steps if available, otherwise use provided ones
  const finalCompletedSteps =
    progressData && Object.keys(progressData).length > 0 ? apiCompletedSteps : completedSteps;

  // Fee payment pending state (link sent but payment not yet done)
  const isFeePaymentPending = useMemo(() => {
    return finalCompletedSteps[9] === true && finalCompletedSteps[10] !== true;
  }, [finalCompletedSteps]);

  // Calculate progress percentage
  const progressPercent = progressData?.ApplicationCompletionPercentage || 0;

  // Filter journey steps based on country
  const filteredJourneySteps = useMemo(() => {
    if (leadCountry?.toLowerCase() === 'india') {
      return JOURNEY_STEPS.filter((step) => step.id !== 3); // Remove "Offer Letter Generated" step
    }
    return JOURNEY_STEPS;
  }, [leadCountry]);

  // Find the next step to be completed (first incomplete step)
  const nextStepToComplete = useMemo(() => {
    for (const step of filteredJourneySteps) {
      if (!finalCompletedSteps[step.id]) {
        return step.id;
      }
    }
    return filteredJourneySteps[filteredJourneySteps.length - 1]?.id; // All steps completed
  }, [finalCompletedSteps, filteredJourneySteps]);

  // Determine if a step is clickable
  const isStepClickable = (stepId) => {
    if (stepId === 2) return true; // Document Upload always enabled
    if (stepId === 3) return true; // Offer Letter always clickable when visible
    if (stepId === 4) return true; // Payment Link always clickable
    if (stepId === 8 && finalCompletedSteps[4] === true) return true; // Interview after payment link
    // Step 9 (Fee Payment Link): clickable after application submitted
    if (stepId === 9 && finalCompletedSteps[7] === true) return true;
    // Step 10 (Fee Payment): clickable after application submitted (allows navigation to fee tab)
    if (stepId === 10 && finalCompletedSteps[7] === true) return true;
    // Other steps are clickable if completed or next to be completed
    return finalCompletedSteps[stepId] === true || stepId === nextStepToComplete;
  };

  const handleStepClick = (stepId) => {
    if (isClickable && isStepClickable(stepId)) {
      onStepClick(stepId);
    }
  };

  // Returns a step-specific status badge for step 10 (fee payment pending/done)
  const getStepBadge = (stepId) => {
    if (stepId === 10 && isFeePaymentPending) {
      return (
        <span className="mt-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:bg-amber-900/40 dark:text-amber-400">
          Pending
        </span>
      );
    }
    return null;
  };

  return (
    <div className={`${isMobile ? 'mb-6' : 'mb-8'}`}>
      <h3
        className={`${
          isMobile ? 'mb-4 text-lg' : 'mb-6 text-xl'
        } dark:text-dark-50 font-semibold text-gray-900`}
      >
        Progress Journey
      </h3>

      {/* Stepper */}
      <div className={`${isMobile ? 'overflow-x-auto pb-3' : ''}`}>
        <div className={`${isMobile ? 'min-w-max px-1' : 'w-full'}`}>

          {/* ── Row 1: Circles + connector lines ── */}
          <div className="flex flex-row items-center w-full">
            {filteredJourneySteps.map((step, index) => {
              const isActive = step.id === currentStep;
              const isCompleted = finalCompletedSteps[step.id] === true;
              const canClickStep = isStepClickable(step.id);
              const isPendingFeePayment = step.id === 10 && isFeePaymentPending;
              const circleSize = isMobile ? 'h-10 w-10' : 'h-12 w-12';

              return (
                <React.Fragment key={`circle-${step.id}`}>
                  {/* Circle button */}
                  <button
                    onClick={() => handleStepClick(step.id)}
                    disabled={!isClickable || !canClickStep}
                    className={`flex-shrink-0 ${circleSize} flex items-center justify-center rounded-full transition-all duration-300
                      ${isActive
                        ? 'bg-primary-600 shadow-lg shadow-primary-200 dark:shadow-primary-900/50 scale-105 text-white'
                        : isCompleted
                          ? 'bg-green-500 text-white'
                          : isPendingFeePayment
                            ? 'border-2 border-amber-400 bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400'
                            : canClickStep
                              ? 'border-2 border-primary-400 dark:border-primary-600 bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 hover:bg-primary-200 dark:hover:bg-primary-900/50'
                              : 'bg-gray-200 dark:bg-dark-600 text-gray-500 dark:text-dark-300 cursor-not-allowed'
                      }
                      ${isClickable && canClickStep ? 'cursor-pointer hover:shadow-md' : 'cursor-not-allowed'}`}
                    title={step.label}
                  >
                    {isCompleted
                      ? <CheckCircleIcon className={isMobile ? 'h-5 w-5' : 'h-6 w-6'} />
                      : React.cloneElement(step.icon, { className: isMobile ? 'w-5 h-5' : 'w-6 h-6' })
                    }
                  </button>

                  {/* Connector line — flex-1 so it stretches between circles */}
                  {index < filteredJourneySteps.length - 1 && (
                    <div
                      className={`flex-1 h-0.5 ${isMobile ? 'mx-1' : 'mx-2'} transition-colors
                        ${finalCompletedSteps[step.id] === true
                          ? 'bg-green-400'
                          : 'bg-gray-200 dark:bg-dark-600'
                        }`}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* ── Row 2: Labels — mirrored flex layout so each label sits under its circle ── */}
          <div className="flex flex-row items-start w-full mt-2">
            {filteredJourneySteps.map((step, index) => {
              const isActive = step.id === currentStep;
              const isCompleted = finalCompletedSteps[step.id] === true;
              const isPendingFeePayment = step.id === 11 && isFeePaymentPending;
              const circleSize = isMobile ? 'w-10' : 'w-12';

              return (
                <React.Fragment key={`label-${step.id}`}>
                  {/* Label cell — same fixed width as the circle above it */}
                  <div className={`flex-shrink-0 ${circleSize} flex flex-col items-center`}>
                    <span
                      className={`text-center leading-tight ${isMobile ? 'text-xs' : 'text-sm'} ${
                        isActive
                          ? 'text-primary-600 dark:text-primary-400 font-medium'
                          : isCompleted
                            ? 'text-green-600 dark:text-green-400'
                            : isPendingFeePayment
                              ? 'font-medium text-amber-600 dark:text-amber-400'
                              : 'text-gray-500 dark:text-dark-400'
                      }`}
                    >
                      {isMobile ? step.shortLabel : step.label}
                    </span>
                    {getStepBadge(step.id)}
                  </div>

                  {/* Spacer matching connector width */}
                  {index < filteredJourneySteps.length - 1 && (
                    <div className={`flex-1 ${isMobile ? 'mx-1' : 'mx-2'}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>

        </div>
      </div>
    </div>
  );
};

export default ProgressJourney;
